/** Pure derivations for the customer home (unit tested). No data is invented here: everything is computed from API rows. */
import type { Tone } from '@qm/ui';

export interface PendingItem { id: string; kind: string; title: string; status: string; createdAt: string }
export interface LeadRow { id: string; channel?: string | null; qualification?: string | null; status?: string | null; createdAt?: string | null }
export interface CalItem { id: string; type: string; title: string; startsAt: string; status?: string | null; overdue?: boolean; local: { date: string; time: string } }
export interface WeekData { start: string; timeZone: string; days: { date: string; items: CalItem[] }[] }

/** Kinds the approvals API can return. Anything else falls back to the generic label. */
export const APPROVAL_KINDS = ['content', 'campaign', 'ai_action', 'whatsapp_draft', 'deliverable', 'cmo_plan', 'caption'] as const;
const KIND_TONE: Record<string, Tone> = { content: 'lavender', caption: 'lavender', campaign: 'coral', ai_action: 'sky', whatsapp_draft: 'mint', deliverable: 'sun', cmo_plan: 'sky' };
export const kindTone = (kind: string): Tone => KIND_TONE[kind] ?? 'lavender';
export const knownKind = (kind: string): string => ((APPROVAL_KINDS as readonly string[]).includes(kind) ? kind : 'other');

export const pendingOnly = (rows: readonly PendingItem[]): PendingItem[] =>
  rows.filter((r) => r.status === 'pending').sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

/** Score band: below 50 coral, below 75 sun, otherwise mint. Always shown next to the number. */
export function scoreTone(score: number): Tone { return score < 50 ? 'coral' : score < 75 ? 'sun' : 'mint'; }
export type Trend = 'up' | 'down' | 'same';
export const trendOf = (delta: number | null): Trend | null => (delta === null ? null : delta > 0 ? 'up' : delta < 0 ? 'down' : 'same');

/** YYYY-MM of an instant in a time zone. */
export function monthKey(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return localDate(d, timeZone).slice(0, 7);
}
/** YYYY-MM-DD of an instant in a time zone. */
export function localDate(d: Date, timeZone: string): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${g('year')}-${g('month')}-${g('day')}`;
}
/** Sunday on or before the local date. */
export function weekStart(now: Date, timeZone: string): string {
  const today = localDate(now, timeZone);
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d - new Date(Date.UTC(y, m - 1, d)).getUTCDay()));
  return dt.toISOString().slice(0, 10);
}

export interface SourceCount { channel: string; count: number }
/** Qualified (sql or mql) leads created this month in the user's time zone, grouped by channel, biggest first. */
export function qualifiedBySource(rows: readonly LeadRow[], now: Date, timeZone: string): { total: number; sources: SourceCount[] } {
  const month = monthKey(now.toISOString(), timeZone);
  const by = new Map<string, number>();
  let total = 0;
  for (const r of rows) {
    if (r.qualification !== 'sql' && r.qualification !== 'mql') continue;
    if (!r.createdAt || monthKey(r.createdAt, timeZone) !== month) continue;
    const ch = r.channel || 'manual';
    by.set(ch, (by.get(ch) ?? 0) + 1);
    total += 1;
  }
  return { total, sources: [...by].map(([channel, count]) => ({ channel, count })).sort((a, b) => b.count - a.count || a.channel.localeCompare(b.channel)) };
}

/** Week items in time order, with the day counts for the strip. */
export function weekSummary(w: WeekData) {
  const days = w.days ?? [];
  const items = days.flatMap((d) => d.items ?? []).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  return { items, counts: days.map((d) => ({ date: d.date, count: (d.items ?? []).length })) };
}

export const CAL_TYPE_ICON: Record<string, 'content' | 'approvals' | 'calendar' | 'ads' | 'inbox'> = {
  deliverable: 'content', approval: 'approvals', post: 'content', campaign: 'ads', meeting: 'calendar', email_campaign: 'inbox', milestone: 'calendar',
};
export const CAL_TYPE_TONE: Record<string, Tone> = { deliverable: 'sun', approval: 'coral', post: 'sky', campaign: 'lavender', meeting: 'mint', email_campaign: 'sky', milestone: 'lavender' };
export const CAL_TYPES = Object.keys(CAL_TYPE_ICON);

/** The six audit factors (qm-audit method): tone and glyph per key, in the order the audit reports them. */
export const FACTOR_TONE: Record<string, Tone> = { search: 'lavender', speed: 'coral', crawl: 'sun', trust: 'sky', mobile: 'mint', social: 'lavender' };
export const FACTOR_ICON: Record<string, 'audit' | 'ads' | 'content' | 'settings' | 'inbox' | 'team'> = { search: 'audit', speed: 'ads', crawl: 'content', trust: 'settings', mobile: 'inbox', social: 'team' };
export const KNOWN_FACTORS = Object.keys(FACTOR_TONE);
export const factorTone = (key: string): Tone => FACTOR_TONE[key] ?? 'lavender';
/** Factors with a score first-class; unmeasured (null) ones stay listed so the gap is visible, never zero-filled. */
export const factorRows = (factors: readonly { key: string; label: string; score: number | null }[] | undefined) => (factors ?? []).map((x) => ({ ...x, tone: factorTone(x.key), known: KNOWN_FACTORS.includes(x.key) }));

/** Lead movement this month (user's time zone): created, qualified (sql or mql) and converted (status "converted"). Merged leads are not counted. */
export function leadMovement(rows: readonly LeadRow[], now: Date, timeZone: string): { created: number; qualified: number; converted: number } {
  const month = monthKey(now.toISOString(), timeZone);
  const out = { created: 0, qualified: 0, converted: 0 };
  for (const r of rows) {
    if (r.status === 'merged' || !r.createdAt || monthKey(r.createdAt, timeZone) !== month) continue;
    out.created += 1;
    if (r.qualification === 'sql' || r.qualification === 'mql') out.qualified += 1;
    if (r.status === 'converted') out.converted += 1;
  }
  return out;
}

/** Deliverables due in the loaded week and how many of them are overdue. */
export function deliverablesDue(w: WeekData): { due: number; overdue: number } {
  const items = (w.days ?? []).flatMap((d) => d.items ?? []).filter((i) => i.type === 'deliverable');
  return { due: items.length, overdue: items.filter((i) => i.overdue).length };
}

export type Greeting = 'morning' | 'afternoon' | 'evening';
/** Time-of-day greeting from the hour in the user's time zone (05-11 morning, 12-17 afternoon, otherwise evening). */
export function greetingOf(now: Date, timeZone: string): Greeting {
  const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(now));
  return h >= 5 && h < 12 ? 'morning' : h >= 12 && h < 18 ? 'afternoon' : 'evening';
}

export interface QueueRow { key: string; kind: 'event' | 'approval'; title: string; time: string | null; type: string; status?: string | null; overdue: boolean; href: string }
/** Today's work queue: today's calendar items in time order, then pending approvals (newest first). Nothing is invented. */
export function todayQueue(week: WeekData | null, approvals: readonly PendingItem[], today: string, limit = 8): QueueRow[] {
  const events: QueueRow[] = (week?.days ?? []).flatMap((d) => d.items ?? []).filter((i) => i.local?.date === today)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
    .map((i) => ({ key: `ev-${i.type}-${i.id}`, kind: 'event' as const, title: i.title, time: i.local?.time ?? '', type: i.type, status: i.status ?? null, overdue: !!i.overdue, href: '/calendar' }));
  const pending: QueueRow[] = pendingOnly([...approvals]).map((a) => ({ key: `ap-${a.id}`, kind: 'approval' as const, title: a.title, time: null, type: a.kind, overdue: false, href: `/approvals/${encodeURIComponent(a.id)}` }));
  return [...events, ...pending].slice(0, limit);
}

/* ------------------------------------------------------------------------------------------------------------------
 * Quixo OS dashboard (8 Oct 2026): windows, deltas, per-day series, status counts, opportunities, schedule tones.
 * All pure, all from API rows; nothing is invented when data is missing (callers render an empty state instead). */

const DAY = 86_400_000;
/** Local YYYY-MM-DD for each of the last `days` days, oldest first, ending today. */
export function lastDays(now: Date, timeZone: string, days: number): string[] {
  return Array.from({ length: days }, (_, i) => localDate(new Date(now.getTime() - (days - 1 - i) * DAY), timeZone));
}

export interface WindowStats { count: number; previous: number; perDay: number[]; days: string[] }
/** Rows created in the last `days` local days vs the `days` before them, plus a per-day series (oldest first). */
export function windowStats<T extends { createdAt?: string | null; status?: string | null }>(rows: readonly T[], now: Date, timeZone: string, days: number, keep: (r: T) => boolean = () => true): WindowStats {
  const cur = lastDays(now, timeZone, days);
  const prev = new Set(lastDays(new Date(now.getTime() - days * DAY), timeZone, days));
  const idx = new Map(cur.map((d, i) => [d, i]));
  const perDay = cur.map(() => 0);
  let count = 0; let previous = 0;
  for (const r of rows) {
    if (r.status === 'merged' || !r.createdAt || !keep(r)) continue;
    const d = localDate(new Date(r.createdAt), timeZone);
    const i = idx.get(d);
    if (i !== undefined) { perDay[i]! += 1; count += 1; } else if (prev.has(d)) previous += 1;
  }
  return { count, previous, perDay, days: cur };
}
/** Percentage change, rounded; null when there is no previous value to compare with. */
export function pctChange(current: number, previous: number): number | null {
  if (previous <= 0) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 100);
}
export const isQualified = (r: { qualification?: string | null }) => r.qualification === 'sql' || r.qualification === 'mql';

export const LEAD_STAGES = ['new', 'contacted', 'qualified', 'converted'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];
/** Lead funnel counts for rows created in the window: new (everything), contacted, qualified, converted. */
export function leadStages(rows: readonly LeadRow[], now: Date, timeZone: string, days: number): Record<LeadStage, number> {
  const keepDays = new Set(lastDays(now, timeZone, days));
  const out: Record<LeadStage, number> = { new: 0, contacted: 0, qualified: 0, converted: 0 };
  for (const r of rows) {
    if (r.status === 'merged' || !r.createdAt || !keepDays.has(localDate(new Date(r.createdAt), timeZone))) continue;
    out.new += 1;
    const s = r.status ?? '';
    if (s === 'contacted' || s === 'qualified' || s === 'converted' || isQualified(r)) out.contacted += 1;
    if (isQualified(r) || s === 'qualified' || s === 'converted') out.qualified += 1;
    if (s === 'converted') out.converted += 1;
  }
  return out;
}
/** Leads per week for the last `weeks` weeks (oldest first). */
export function weeklyCounts(rows: readonly LeadRow[], now: Date, timeZone: string, weeks: number): number[] {
  const days = lastDays(now, timeZone, weeks * 7);
  const idx = new Map(days.map((d, i) => [d, Math.floor(i / 7)]));
  const out = Array.from({ length: weeks }, () => 0);
  for (const r of rows) {
    if (r.status === 'merged' || !r.createdAt) continue;
    const w = idx.get(localDate(new Date(r.createdAt), timeZone));
    if (w !== undefined) out[w]! += 1;
  }
  return out;
}

export type Impact = 'high' | 'medium' | 'low';
export interface Opportunity { key: string; label: string; score: number; impact: Impact; known: boolean }
/** Audit factors ranked by how much they hold the score back: lowest first; unmeasured factors are left out. */
export function opportunities(factors: readonly { key: string; label: string; score: number | null }[] | undefined, limit = 5): Opportunity[] {
  return (factors ?? [])
    .filter((f): f is { key: string; label: string; score: number } => typeof f.score === 'number')
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((f) => ({ key: f.key, label: f.label, score: f.score, impact: f.score < 50 ? 'high' : f.score < 75 ? 'medium' : 'low', known: KNOWN_FACTORS.includes(f.key) }));
}

export type ScheduleTone = 'positive' | 'info' | 'warning' | 'accent' | 'neutral';
export const SCHEDULE_STATUSES = ['published', 'in_progress', 'pending', 'scheduled', 'done', 'meeting'] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];
/** Status chip for a calendar item: explicit status first, then the type. */
export function scheduleStatus(item: Pick<CalItem, 'type' | 'status' | 'overdue'>): { status: ScheduleStatus; tone: ScheduleTone } {
  const s = (item.status ?? '').toLowerCase();
  if (['published', 'delivered', 'approved', 'completed', 'done'].includes(s)) return { status: s === 'done' || s === 'completed' ? 'done' : 'published', tone: 'positive' };
  if (['in_progress', 'in-progress', 'review', 'in_review', 'drafting'].includes(s)) return { status: 'in_progress', tone: 'info' };
  if (['pending', 'awaiting', 'awaiting_approval', 'submitted'].includes(s) || item.type === 'approval' || item.overdue) return { status: 'pending', tone: 'warning' };
  if (item.type === 'meeting') return { status: 'meeting', tone: 'accent' };
  return { status: 'scheduled', tone: 'neutral' };
}

export interface ConnectionLite { provider: string; status: string }
/** One chip per provider: connected if any connection for it is, otherwise the first status seen. */
export function providerSummary(rows: readonly ConnectionLite[]): { provider: string; connected: boolean }[] {
  const by = new Map<string, boolean>();
  for (const r of rows) by.set(r.provider, (by.get(r.provider) ?? false) || r.status === 'connected');
  return [...by].map(([provider, connected]) => ({ provider, connected })).sort((a, b) => Number(b.connected) - Number(a.connected) || a.provider.localeCompare(b.provider));
}

/** Leads created in the last `days` days (user's time zone), grouped by channel, biggest first. */
export function leadsBySource(rows: readonly LeadRow[], now: Date, timeZone: string, days: number): SourceCount[] {
  const keep = new Set(lastDays(now, timeZone, days));
  const by = new Map<string, number>();
  for (const r of rows) {
    if (!r.createdAt || !keep.has(localDate(new Date(r.createdAt), timeZone))) continue;
    const ch = r.channel || 'manual';
    by.set(ch, (by.get(ch) ?? 0) + 1);
  }
  return [...by].map(([channel, count]) => ({ channel, count })).sort((a, b) => b.count - a.count || a.channel.localeCompare(b.channel));
}

/**
 * Leads per weekday (rows, Monday first) per week (columns, oldest first) over the last `weeks` weeks, in the user's
 * time zone. Week columns are 7-day blocks ending today, so the right-most column is always the current week.
 */
export function weekdayHeat(rows: readonly LeadRow[], now: Date, timeZone: string, weeks: number): { values: number[][]; weekStarts: string[] } {
  const days = lastDays(now, timeZone, weeks * 7);
  const index = new Map(days.map((d, i) => [d, i] as const));
  const values = Array.from({ length: 7 }, () => Array.from({ length: weeks }, () => 0));
  for (const r of rows) {
    if (!r.createdAt) continue;
    const d = localDate(new Date(r.createdAt), timeZone);
    const i = index.get(d);
    if (i === undefined) continue;
    const dow = (new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
    values[dow]![Math.floor(i / 7)]! += 1;
  }
  return { values, weekStarts: days.filter((_, i) => i % 7 === 0) };
}
